import { ref } from 'vue'

/**
 * 發票相關 polling 工具
 * 使用 setTimeout 遞迴（非 setInterval），搭配 generation counter 防止 stale response。
 * SD 規格：
 *   - 列印狀態：2s / 15 次（30s）
 *   - 測試列印：3s / 10 次（30s）
 *   - POS 開票：2s / 15 次（30s）
 */

const POLLING_PRESETS = {
  print: { interval: 2000, maxAttempts: 15 },
  testPrint: { interval: 3000, maxAttempts: 10 },
  invoiceResult: { interval: 2000, maxAttempts: 15 },
}

export const useInvoicePolling = () => {
  // 每個 polling context 獨立 timer + generation
  let gen = 0
  let timer = null
  const isPolling = ref(false)

  /**
   * 停止目前 polling
   */
  const stopPolling = () => {
    gen += 1
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    isPolling.value = false
  }

  /**
   * 通用 polling 引擎
   * @param {Object} options
   * @param {Function} options.pollFn - 每次呼叫的 async 函式，回傳 [data, err]
   * @param {Function} options.isTerminal - 判斷是否到達終態 (data) => boolean
   * @param {number} [options.interval=2000] - 每次 poll 間隔 ms
   * @param {number} [options.maxAttempts=15] - 最大嘗試次數
   * @param {Function} [options.onUpdate] - 每次 poll 成功後 callback(data)
   * @param {Function} [options.onDone] - 到達終態 callback(data)
   * @param {Function} [options.onTimeout] - 超過 maxAttempts callback()
   * @param {Function} [options.onError] - poll 失敗 callback(err)
   * @returns {Function} stop - 停止 polling 的函式
   */
  const startPolling = ({
    pollFn,
    isTerminal,
    interval = 2000,
    maxAttempts = 15,
    onUpdate,
    onDone,
    onTimeout,
    onError,
  } = {}) => {
    stopPolling()

    const myGen = ++gen
    let attempts = 0
    isPolling.value = true

    const poll = async () => {
      attempts += 1
      const [data, err] = await pollFn()

      if (myGen !== gen) return

      if (err) {
        if (onError) onError(err)
        // API 失敗不停止 polling，繼續嘗試直到 maxAttempts
        if (attempts < maxAttempts) {
          timer = setTimeout(poll, interval)
        } else {
          isPolling.value = false
          if (onTimeout) onTimeout()
        }
        return
      }

      if (onUpdate) onUpdate(data)

      if (isTerminal(data)) {
        isPolling.value = false
        if (onDone) onDone(data)
        return
      }

      if (attempts >= maxAttempts) {
        isPolling.value = false
        if (onTimeout) onTimeout()
        return
      }

      timer = setTimeout(poll, interval)
    }

    poll()

    return stopPolling
  }

  /**
   * 列印狀態 polling（2s / 15 次）
   * @param {Object} options
   * @param {Function} options.pollFn - 回傳 [data, err]
   * @param {Function} options.isTerminal - (data) => boolean
   * @param {Function} [options.onUpdate]
   * @param {Function} [options.onDone]
   * @param {Function} [options.onTimeout]
   * @param {Function} [options.onError]
   * @returns {Function} stop
   */
  const pollPrintStatus = ({ pollFn, isTerminal, onUpdate, onDone, onTimeout, onError } = {}) => {
    return startPolling({
      pollFn,
      isTerminal,
      interval: POLLING_PRESETS.print.interval,
      maxAttempts: POLLING_PRESETS.print.maxAttempts,
      onUpdate,
      onDone,
      onTimeout,
      onError,
    })
  }

  /**
   * 測試列印 polling（3s / 10 次）
   */
  const pollTestPrintStatus = ({ pollFn, isTerminal, onUpdate, onDone, onTimeout, onError } = {}) => {
    return startPolling({
      pollFn,
      isTerminal,
      interval: POLLING_PRESETS.testPrint.interval,
      maxAttempts: POLLING_PRESETS.testPrint.maxAttempts,
      onUpdate,
      onDone,
      onTimeout,
      onError,
    })
  }

  /**
   * POS 開票結果 polling（2s / 15 次）
   */
  const pollInvoiceResult = ({ pollFn, isTerminal, onUpdate, onDone, onTimeout, onError } = {}) => {
    return startPolling({
      pollFn,
      isTerminal,
      interval: POLLING_PRESETS.invoiceResult.interval,
      maxAttempts: POLLING_PRESETS.invoiceResult.maxAttempts,
      onUpdate,
      onDone,
      onTimeout,
      onError,
    })
  }

  return {
    isPolling,
    startPolling,
    stopPolling,
    pollPrintStatus,
    pollTestPrintStatus,
    pollInvoiceResult,
    POLLING_PRESETS,
  }
}
